import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import {useToast} from '../context/ToastContext';
import {fetchRef} from "../service/refsCache";
import {useWardrobe} from "../context/WardrobeContext";
import useOverlayClose from "../hooks/useOverlayClose";
import useDialogA11y from "../hooks/useDialogA11y";
import {
    buildItemPayload,
    EMPTY_ITEM_FORM_VALUES,
    useItemForm,
    valuesFromEditItem,
    valuesFromPrefill,
    type ItemFormErrorField,
    type ItemFormValues,
} from "../hooks/useItemForm";
import {
    EMPTY_ITEM_FORM_REFS,
    type BrandOption,
    type ClothingItemEditData,
    type ImageSlot,
    type ItemFormRefs,
    type RefOption,
} from "./item-form/types";
import {
    initialSectionState,
    SECTION_BY_ERROR_FIELD,
    SECTION_TITLES,
    type ItemFormMode,
    type ItemFormSectionId,
    type SectionOpenState,
} from "./item-form/sections";
import ItemFormSection from "./item-form/ItemFormSection";
import ItemFormPhotos from "./item-form/ItemFormPhotos";
import ItemFormBasics from "./item-form/ItemFormBasics";
import ItemFormTraits from "./item-form/ItemFormTraits";
import ItemFormWearContext from "./item-form/ItemFormWearContext";
import ItemFormCondition from "./item-form/ItemFormCondition";
import ItemFormLifecycle from "./item-form/ItemFormLifecycle";
import {XIcon} from "./icons";

export type {ClothingItemEditData} from "./item-form/types";

interface ClothingItemListDto {
    id: string;
    name?: string;
    [key: string]: unknown;
}

interface AddClothingItemModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    editItem?: ClothingItemEditData | null;
    renderAsPage?: boolean;
    aiConfirmRequestId?: string;
    aiConfirmUpdateMode?: boolean;
    /** Найденные распознаванием недостатки (recognition-v5) — плашка в режиме проверки AI. */
    aiDetectedFlaws?: string[] | null;
    onAiConfirmSuccess?: (item: ClothingItemListDto) => void;
    /** Предзаполнение полей при создании НОВОЙ вещи (не редактирование). CTHS-200. */
    prefill?: Partial<ClothingItemEditData> | null;
    /** Вызывается с созданной вещью при создании новой (не редактировании). */
    onCreated?: (item: ClothingItemListDto) => void;
    /** Альтернативный путь с первого экрана: отдать фото распознаванию. */
    onRecognizeByPhoto?: () => void;
}

const TITLE_ID = 'add-item-modal-title';

/** Поле, на которое ведёт строка summary с ошибкой. */
const ERROR_FIELD_INPUT_ID: Record<ItemFormErrorField, string> = {
    name: 'item-name',
    priceRub: 'item-price',
};

/**
 * Форма вещи: создание, редактирование и проверка результата AI.
 *
 * Контейнер: режимы, справочники, фотографии, отправка, диалоговая обёртка.
 * Значения и валидация — в `useItemForm`, разметка полей — в «тупых» секциях
 * `components/item-form/*`. Раньше здесь было 997 строк и 25 `useState`, а все
 * поля разом падали на пользователя (QA-13).
 */
const AddClothingItemModal: React.FC<AddClothingItemModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    editItem,
    renderAsPage = false,
    aiConfirmRequestId,
    aiConfirmUpdateMode = false,
    aiDetectedFlaws,
    onAiConfirmSuccess,
    prefill,
    onCreated,
    onRecognizeByPhoto
}) => {
    const {selectedOwnerId} = useWardrobe();
    const {showToast} = useToast();
    const form = useItemForm();
    const {values, errors, setField, toggle, reset, validate} = form;

    const mode: ItemFormMode = aiConfirmRequestId ? 'aiConfirm' : (editItem ? 'edit' : 'create');
    const isCreate = mode === 'create';

    const [refs, setRefs] = useState<ItemFormRefs>(EMPTY_ITEM_FORM_REFS);
    const [images, setImages] = useState<ImageSlot[]>([]);
    const [sectionsOpen, setSectionsOpen] = useState<SectionOpenState>(
        () => initialSectionState(mode, EMPTY_ITEM_FORM_VALUES)
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const dialogRef = useRef<HTMLDivElement>(null);

    const setBrands = useCallback((brands: BrandOption[]) => {
        setRefs(prev => ({...prev, brands}));
    }, []);

    const loadRefs = useCallback(async () => {
        const [
            categories, sizes, conditions, colors, brands,
            layers, warmthLevels, seasons, materials, occasions,
            styles, weatherSuitabilities, defects,
            printTypes, fits, formalities
        ] = await Promise.all([
            fetchRef<RefOption[]>('categories'),
            fetchRef<RefOption[]>('sizes'),
            fetchRef<RefOption[]>('conditions'),
            fetchRef<RefOption[]>('colors'),
            fetchRef<BrandOption[]>('brands', selectedOwnerId ? {ownerId: selectedOwnerId} : undefined),
            fetchRef<RefOption[]>('layers'),
            fetchRef<RefOption[]>('warmth-levels'),
            fetchRef<RefOption[]>('seasons'),
            fetchRef<RefOption[]>('materials'),
            fetchRef<RefOption[]>('occasions'),
            fetchRef<RefOption[]>('styles'),
            fetchRef<RefOption[]>('weather-suitabilities'),
            fetchRef<RefOption[]>('defects'),
            fetchRef<RefOption[]>('print-types'),
            fetchRef<RefOption[]>('fits'),
            fetchRef<RefOption[]>('formalities'),
        ]);
        setRefs({
            categories, sizes, conditions, colors, brands,
            layers, warmthLevels, seasons, materials, occasions,
            styles, weatherSuitabilities, defects,
            printTypes, fits, formalities,
        });
    }, [selectedOwnerId]);

    useEffect(() => {
        if (isOpen) {
            loadRefs();
        }
    }, [isOpen, loadRefs]);

    const editItemKey = editItem?.id ?? (editItem === null ? 'empty' : 'none');

    /**
     * Открытие формы: значения, фотографии и раскрытые секции задаются один раз.
     * `prefill` читаем в момент открытия — в зависимостях его нет, иначе новый
     * объект на каждый рендер сбрасывал бы ввод.
     */
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setError(null);
        const initialValues: ItemFormValues = editItem
            ? valuesFromEditItem(editItem)
            : valuesFromPrefill(prefill);
        reset(initialValues);
        setSectionsOpen(initialSectionState(mode, initialValues));
        setImages((editItem?.photoUrls ?? []).map((key, i) => ({
            id: `img-${i}-${key}`,
            key,
            previewUrl: ''
        })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, editItemKey]);

    const toggleSection = useCallback((id: ItemFormSectionId) => {
        setSectionsOpen(prev => ({...prev, [id]: !prev[id]}));
    }, []);

    const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (files.length === 0) return;

        const api = await createAxiosInstance();
        const newSlots: ImageSlot[] = [];

        for (const file of files) {
            if (!file.type.startsWith('image/')) continue;
            newSlots.push({
                id: `img-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                file,
                previewUrl: URL.createObjectURL(file),
                uploading: true
            });
        }

        setImages(prev => [...prev, ...newSlots]);

        for (const slot of newSlots) {
            const formData = new FormData();
            formData.append('file', slot.file!);
            try {
                const res = await api.post<{ key: string }>(
                    '/clothing/images',
                    formData,
                    {headers: {'Content-Type': 'multipart/form-data'}}
                );
                setImages(prev => prev.map(s =>
                    s.id === slot.id ? {...s, key: res.data.key, uploading: false} : s
                ));
            } catch (err: unknown) {
                setImages(prev => prev.map(s => {
                    if (s.id === slot.id) {
                        URL.revokeObjectURL(s.previewUrl);
                        return {...s, error: (err as Error).message, uploading: false};
                    }
                    return s;
                }));
            }
        }
    }, []);

    const moveImage = useCallback((index: number, dir: 'up' | 'down') => {
        setImages(prev => {
            const next = [...prev];
            const j = dir === 'up' ? index - 1 : index + 1;
            if (j < 0 || j >= next.length) return prev;
            [next[index], next[j]] = [next[j], next[index]];
            return next;
        });
    }, []);

    const removeImage = useCallback((index: number) => {
        setImages(prev => {
            const slot = prev[index];
            if (slot.previewUrl) URL.revokeObjectURL(slot.previewUrl);
            return prev.filter((_, i) => i !== index);
        });
    }, []);

    /** Ошибки полей открывают свои секции и ведут фокус на первое проблемное поле. */
    const revealErrors = useCallback((fields: ItemFormErrorField[]) => {
        setSectionsOpen(prev => fields.reduce(
            (acc, field) => ({...acc, [SECTION_BY_ERROR_FIELD[field]]: true}),
            {...prev}
        ));
        const first = fields[0];
        if (first) {
            requestAnimationFrame(() => document.getElementById(ERROR_FIELD_INPUT_ID[first])?.focus());
        }
    }, []);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const validationErrors = validate();
        const invalidFields = Object.keys(validationErrors) as ItemFormErrorField[];
        if (invalidFields.length > 0) {
            revealErrors(invalidFields);
            return;
        }

        if (images.some(s => s.uploading)) {
            setError('Дождитесь загрузки всех изображений');
            return;
        }

        setSaving(true);
        try {
            const api = await createAxiosInstance();
            const photoKeys = images.filter(s => s.key && !s.error).map(s => s.key!);
            const payload = buildItemPayload(values, photoKeys);
            if (aiConfirmRequestId) {
                const endpoint = aiConfirmUpdateMode
                    ? `/ai-recognition/${aiConfirmRequestId}/confirm-update`
                    : `/ai-recognition/${aiConfirmRequestId}/confirm`;
                const res = await api.post(endpoint, payload);
                onAiConfirmSuccess?.(res.data);
            } else if (editItem?.id) {
                await api.put(`/clothing/items/${editItem.id}`, payload);
                showToast('Изменения сохранены', 'success');
            } else {
                const res = await api.post('/clothing/items', payload);
                onCreated?.(res.data);
                showToast(`Вещь «${payload.name}» добавлена в гардероб`, 'success');
            }
            onSuccess();
            onClose();
            if (!editItem) {
                reset(EMPTY_ITEM_FORM_VALUES);
                setImages(prev => {
                    prev.forEach(s => s.previewUrl && URL.revokeObjectURL(s.previewUrl));
                    return [];
                });
            }
        } catch (err: unknown) {
            setError((err as Error).message || 'Ошибка сохранения');
        } finally {
            setSaving(false);
        }
    }, [validate, revealErrors, images, values, aiConfirmRequestId, aiConfirmUpdateMode,
        onAiConfirmSuccess, editItem, showToast, onCreated, onSuccess, onClose, reset]);

    const handleClose = useCallback(() => {
        images.forEach(s => s.previewUrl && URL.revokeObjectURL(s.previewUrl));
        setImages([]);
        onClose();
    }, [images, onClose]);

    useDialogA11y(dialogRef, handleClose, {
        active: isOpen,
        trapFocus: !renderAsPage,
        restoreFocus: !renderAsPage,
    });
    const overlayClose = useOverlayClose(handleClose, !saving);

    const errorEntries = useMemo(
        () => (Object.entries(errors) as [ItemFormErrorField, string][]),
        [errors]
    );

    if (!isOpen) return null;

    const title = aiConfirmRequestId
        ? 'Проверить результат AI'
        : (editItem ? 'Редактировать вещь' : 'Добавить вещь');

    const section = (id: ItemFormSectionId, children: React.ReactNode) => (
        <ItemFormSection
            id={id}
            title={SECTION_TITLES[id]}
            open={sectionsOpen[id]}
            onToggle={() => toggleSection(id)}
        >
            {children}
        </ItemFormSection>
    );

    const alerts = (
        <>
            {(error || errorEntries.length > 0) && (
                <div
                    role="alert"
                    data-testid="item-form-error-summary"
                    className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm space-y-1"
                >
                    {error && <p>{error}</p>}
                    {errorEntries.length > 0 && (
                        <>
                            <p className="font-medium">Проверьте заполнение формы</p>
                            <ul className="list-disc pl-5">
                                {errorEntries.map(([field, message]) => (
                                    <li key={field}>
                                        <button
                                            type="button"
                                            className="underline"
                                            onClick={() => revealErrors([field])}
                                        >
                                            {message}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            )}

            {/* recognition-v5: найденные AI недостатки — явное честное перечисление на ревью */}
            {aiConfirmRequestId && aiDetectedFlaws && aiDetectedFlaws.length > 0 && (
                <div
                    className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-sm"
                    data-testid="ai-detected-flaws"
                >
                    <p className="font-medium text-amber-800 dark:text-amber-300 mb-1">
                        Найденные недостатки
                    </p>
                    <ul className="list-disc pl-5 space-y-0.5 text-amber-700 dark:text-amber-200">
                        {aiDetectedFlaws.map((flaw) => (
                            <li key={flaw}>{flaw}</li>
                        ))}
                    </ul>
                </div>
            )}
        </>
    );

    const formBody = (
        <>
            {alerts}
            {/* Первый экран: фото → название → категория → «Добавить». */}
            <div className="space-y-4">
                {section('photos', (
                    <ItemFormPhotos
                        images={images}
                        onSelectFiles={handleFileSelect}
                        onMove={moveImage}
                        onRemove={removeImage}
                        onRecognizeByPhoto={isCreate ? onRecognizeByPhoto : undefined}
                    />
                ))}
                {section('basics', (
                    <ItemFormBasics values={values} errors={errors} refs={refs} setField={setField}/>
                ))}
            </div>

            {/* Дополнительные параметры — на `form.sectionGap` ниже, свёрнуты по умолчанию. */}
            <div className="pt-8 space-y-4">
                {section('traits', (
                    <ItemFormTraits
                        values={values}
                        errors={errors}
                        refs={refs}
                        setField={setField}
                        toggle={toggle}
                        onBrandsUpdate={setBrands}
                        ownerId={selectedOwnerId ?? undefined}
                    />
                ))}
                {section('wearContext', (
                    <ItemFormWearContext values={values} refs={refs} setField={setField} toggle={toggle}/>
                ))}
                {section('condition', (
                    <ItemFormCondition values={values} refs={refs} setField={setField} toggle={toggle}/>
                ))}
                {/* Новая вещь не может быть уже выброшена — в create секции нет. */}
                {!isCreate && section('lifecycle', (
                    <ItemFormLifecycle values={values} setField={setField}/>
                ))}
            </div>
        </>
    );

    const footer = (
        <div className="shrink-0 flex gap-2 px-4 py-3 border-t border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800">
            <button
                type="button"
                onClick={handleClose}
                data-testid="item-form-cancel"
                className="btn-secondary flex-1 sm:flex-none min-h-[48px] sm:ml-auto"
            >
                {renderAsPage ? 'Назад' : 'Отмена'}
            </button>
            <button
                type="submit"
                disabled={saving}
                data-testid="item-form-submit"
                className="btn-primary flex-1 sm:flex-none min-h-[48px]"
            >
                {saving ? 'Сохранение...' : (editItem ? 'Сохранить' : 'Добавить')}
            </button>
        </div>
    );

    const header = (
        <div className="shrink-0 flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-white/10">
            <h2 id={TITLE_ID} className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {title}
            </h2>
            {!renderAsPage && (
                <button
                    type="button"
                    onClick={handleClose}
                    data-testid="item-form-close"
                    aria-label="Закрыть"
                    className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 pressable"
                >
                    <XIcon className="w-6 h-6"/>
                </button>
            )}
        </div>
    );

    if (renderAsPage) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full">
                {header}
                <form onSubmit={handleSubmit}>
                    <div className="p-4">{formBody}</div>
                    {footer}
                </form>
            </div>
        );
    }

    return (
        <div
            className="fw-overlay fixed inset-0 z-50 flex items-stretch justify-center sm:items-center sm:p-4"
            {...overlayClose}
        >
            <div className="absolute inset-0 bg-black/50" aria-hidden/>
            {/* < sm — лист на весь экран со закреплённым футером (styleguide §Overlays). */}
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={TITLE_ID}
                tabIndex={-1}
                className="relative z-10 flex flex-col w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-2xl
                           bg-white dark:bg-gray-800 shadow-xl sm:rounded-3xl overflow-hidden
                           focus:outline-none animate-pop-in"
            >
                {header}
                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                    <div className="flex-1 min-h-0 overflow-y-auto p-4">{formBody}</div>
                    {footer}
                </form>
            </div>
        </div>
    );
};

export default AddClothingItemModal;
